import React, {
  useEffect, useState,
} from 'react'
import { Redirect } from 'react-router-dom'
import { HeaderNavi } from '../../components/navigation'
import InlineLoadingAnimation from '../../components/common/InlineLoadingAnimation'
import { loginLogoIcon } from '../../icons'
import { OVERVIEW } from '../../constants/Paths'
import { getAccessToken } from '../../modules/common/session'
import {
  getOfficeLoginConfiguration,
  OfficeLoginConfiguration,
} from '../../modules/OfficeLoginConfiguration'
import Login from './Login'
import './Login.css'

const LOGIN_CONFIGURATION_TIMEOUT_MS = 10 * 1000
const MICROSOFT_MY_APPS_URL = 'https://myapps.microsoft.com/'
const LOGIN_CONFIGURATION_ERROR_MESSAGE = 'ログイン方法を確認できませんでした。通信環境を確認して、もう一度お試しください。'
const NO_LOGIN_METHOD_ERROR_MESSAGE = 'このチームで利用できるログイン方法が設定されていません。管理者にお問い合わせください。'

type Props = {
  navigateToSaml?: (url: string) => void
}

type LoginConfigurationState =
  | { type: 'loading' }
  | { type: 'ready', configuration: OfficeLoginConfiguration }
  | { type: 'error', message: string }

type StatusViewProps = {
  isError: boolean
  isLoading: boolean
  message: string
  onRetry: (() => void) | null
}

const defaultNavigateToSaml = (url: string) => {
  window.location.replace(url)
}

const LoginConfigurationStatus = ({
  isError,
  isLoading,
  message,
  onRetry,
}: StatusViewProps) => (
  <div className="login-content">
    <HeaderNavi />
    <div
      className="mamorio-login-form mamorio-login-status"
      role={isError ? 'alert' : 'status'}
      aria-live="polite"
      data-testid="login-configuration-status"
    >
      <img src={loginLogoIcon} className="mamorio-login-logo" alt="office-logo" />
      {isLoading && (
        <InlineLoadingAnimation
          size="32px"
          color="#4ca7db"
        />
      )}
      <p className="mamorio-login-status-message">
        {message}
      </p>
      {onRetry && (
        <button
          type="button"
          className="btn btn-primary btn-lg mamorio-login-status-action"
          onClick={onRetry}
          data-testid="login-configuration-retry"
        >
          再試行
        </button>
      )}
    </div>
  </div>
)

const LoginConfigurationGate = ({ navigateToSaml }: Props) => {
  const navigate = navigateToSaml || defaultNavigateToSaml
  const hasAccessToken = getAccessToken() !== null
  const [retryCount, setRetryCount] = useState(0)
  const [state, setState] = useState<LoginConfigurationState>({ type: 'loading' })

  useEffect(() => {
    if (hasAccessToken) return undefined

    let isActive = true
    const abortController = new AbortController()
    const timeout = window.setTimeout(
      () => abortController.abort(),
      LOGIN_CONFIGURATION_TIMEOUT_MS,
    )

    setState({ type: 'loading' })

    getOfficeLoginConfiguration(abortController.signal)
      .then((configuration) => {
        if (!isActive) return

        if (!configuration.samlLoginEnabled && !configuration.passwordLoginEnabled) {
          setState({ type: 'error', message: NO_LOGIN_METHOD_ERROR_MESSAGE })
          return
        }

        setState({ type: 'ready', configuration })
      })
      .catch(() => {
        if (isActive) {
          setState({ type: 'error', message: LOGIN_CONFIGURATION_ERROR_MESSAGE })
        }
      })
      .finally(() => window.clearTimeout(timeout))

    return () => {
      isActive = false
      window.clearTimeout(timeout)
      abortController.abort()
    }
  }, [hasAccessToken, retryCount])

  if (hasAccessToken) return <Redirect to={OVERVIEW} />

  if (state.type === 'loading') {
    return (
      <LoginConfigurationStatus
        isError={false}
        isLoading
        message="ログイン方法を確認しています"
        onRetry={null}
      />
    )
  }

  if (state.type === 'error') {
    return (
      <LoginConfigurationStatus
        isError
        isLoading={false}
        message={state.message}
        onRetry={() => setRetryCount((count) => count + 1)}
      />
    )
  }

  const { configuration } = state
  const handleSamlLogin = configuration.samlLoginEnabled
    ? () => navigate(MICROSOFT_MY_APPS_URL)
    : undefined

  return (
    <Login
      onSamlLogin={handleSamlLogin}
      passwordLoginEnabled={configuration.passwordLoginEnabled}
    />
  )
}

LoginConfigurationGate.defaultProps = {
  navigateToSaml: undefined,
}

export default LoginConfigurationGate
